/**
 * ApprovalCard — inline, editable approval card for agent actions.
 * Shows action details with editable fields, approve/reject/edit buttons.
 * Replaces the modal-based ActionConfirmation for inline sidebar use.
 */
import { useState } from "react";
import { Check, X, Pencil, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { AgentOrb } from "./AgentOrb";

export interface ApprovalField {
  key: string;
  label: string;
  value: string;
  editable?: boolean;
}

interface ApprovalCardProps {
  title: string;
  fields: ApprovalField[];
  total?: string;
  riskLevel?: "safe" | "medium" | "high";
  onApprove: (fields: ApprovalField[]) => void;
  onReject: () => void;
  isLoading?: boolean;
  className?: string;
}

export const ApprovalCard = ({
  title,
  fields: initialFields,
  total,
  riskLevel = "medium",
  onApprove,
  onReject,
  isLoading = false,
  className,
}: ApprovalCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [fields, setFields] = useState(initialFields);

  const riskStyles = {
    safe: { className: "bg-green4 text-green2 border-green4", label: "SAFE" },
    medium: { className: "bg-orange4 text-orange1 border-orange4", label: "MEDIUM" },
    high: { className: "bg-red4 text-red2 border-red4", label: "HIGH RISK" },
  };

  const risk = riskStyles[riskLevel];

  const handleFieldChange = (key: string, value: string) => {
    setFields((prev) =>
      prev.map((f) => (f.key === key ? { ...f, value } : f))
    );
  };

  return (
    <div
      className={cn("mt-2 rounded-xl overflow-hidden border border-gray6 bg-white", className)}
    >
      {/* Header */}
      <div className="px-3.5 py-2.5 flex items-center gap-2.5 border-b border-gray6 bg-gradient-to-r from-green4/30 to-white">
        <AgentOrb state="thinking" size={20} />
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-bold text-black1">{title}</div>
          <div className="text-[10px] text-gray3">
            {isEditing ? "Edit fields below, then approve" : "Review and approve"}
          </div>
        </div>
        <Badge className={cn("text-[9px] font-bold", risk.className)}>
          {risk.label}
        </Badge>
      </div>

      {/* Fields */}
      <div className="px-3.5 py-2.5 space-y-2">
        {fields.map((f) => (
          <div key={f.key} className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold text-gray3 uppercase tracking-wide">
              {f.label}
            </span>
            {isEditing && f.editable ? (
              <Input
                type="text"
                value={f.value}
                onChange={(e) => handleFieldChange(f.key, e.target.value)}
                className="text-[12.5px] font-semibold text-black1 bg-gray7 h-7 w-[140px] text-right"
              />
            ) : (
              <span className="text-[12.5px] font-semibold text-black1">
                {f.value}
              </span>
            )}
          </div>
        ))}

        {total && (
          <div className="flex items-center justify-between gap-2 pt-2 mt-2 border-t border-gray6">
            <span className="text-[11px] font-bold text-gray2 uppercase tracking-wide">
              Total
            </span>
            <span className="text-[14px] font-bold text-green2">{total}</span>
          </div>
        )}
      </div>

      {/* Warning */}
      <div className="px-3.5 py-2 bg-[#FFF8E6] border-t border-[#F5D68A] flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-[#F5A623] shrink-0" />
        <span className="text-[10.5px] text-[#7A4F00]">
          This action will make changes. Review carefully.
        </span>
      </div>

      {/* Actions */}
      <div className="px-3.5 py-2.5 flex items-center gap-2 border-t border-gray6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsEditing(!isEditing)}
          disabled={isLoading}
          className="text-[11.5px] h-7 gap-1.5 text-gray2"
        >
          <Pencil className="h-3 w-3" />
          {isEditing ? "Done editing" : "Edit"}
        </Button>
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          onClick={onReject}
          disabled={isLoading}
          className="text-[11.5px] h-7 gap-1.5 text-red2 hover:bg-red4 hover:text-red2"
        >
          <X className="h-3 w-3" />
          Reject
        </Button>
        <Button
          size="sm"
          onClick={() => onApprove(fields)}
          disabled={isLoading}
          className="text-[11.5px] h-7 gap-1.5 bg-green2 hover:bg-green1"
        >
          <Check className="h-3 w-3" />
          {isLoading ? "Running…" : "Approve"}
        </Button>
      </div>
    </div>
  );
};
